import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Add,
  Button,
  Delete,
  LabelValue,
  OrDivider,
  PageTitleAndSubTitle,
  Stats,
  Switch,
  TabStepper,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { useEffect, useState } from 'react';
import DateFilter from '../../_components/molecules/DateFilter';
import { useStores } from '../../stores';
import { observer } from 'mobx-react';
import TrendAnalysisConstants from '../../core-utils/messages/TrendAnalysisConstants';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ChartConfigDropdownsProps,
  ColorPickerCircle,
  getDynamicTimeGrainOptions,
  getFilteredChartStyles,
  getFilteredChartTypes,
  getMetricDropdownOptions,
  getRandomColor,
  getSubMetricDropdownOptions,
  MetricConfigProps,
  SubmetricEntry,
  trendSeriesData,
} from '../../core-utils/helper-utils/trendAnalysisHelper';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate } from 'react-router-dom';
import { TREND_ANALYSIS_INTERNAL } from '../../core-utils/routes';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import BasicModal from '../../_components/atoms/BasicModal';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { cohortComparisonMessages } from '../../core-utils/messages/cohortComparisonPageConstants';
import { CircularLoader } from '@HomaHealth/ui-component-library';

interface TrendAnalysisModalProps {
  isModal?: boolean;
  open?: boolean;
  handleClose?: any;
  modalPrimaryAxisData?: trendSeriesData;
  modalSecondaryAxisData?: trendSeriesData;
  modalIsSecondaryAxis?: boolean;
  modalTimeGrain?: string;
  handleModalSubmit?: any;
  isCohortComparison?: boolean;
  isEdit?: boolean;
}

const TrendAnalysisPage: React.FC<TrendAnalysisModalProps> = observer(
  ({
    isModal,
    open,
    handleClose,
    modalPrimaryAxisData,
    modalSecondaryAxisData,
    modalIsSecondaryAxis,
    modalTimeGrain,
    handleModalSubmit,
    isCohortComparison,
    isEdit,
  }) => {
    const { classes } = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 150px 40px 60px',
      },
      title: {
        marginBottom: '20px',
        position: 'relative',
      },
      stepsGrid: {
        background: patientAccessTheme.palette.status.white,
        border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderRadius: '4px',
        width: '100%',
      },
      content: {
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        padding: '30px',
        height: isModal ? '460px' : '',
        overflowY: isModal ? 'scroll' : 'visible',
        ...scrollBarStyles,
      },
      metricBuilder: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        width: '100%',
      },
      innerContainer: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        height: '40px',
      },
      wrap: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
      primaryAxis: {
        borderRight: isCohortComparison
          ? ''
          : `solid 1px ${patientAccessTheme.palette.grey[300]}`,
        width: '50%',
        paddingRight: '30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      },
      metricSelection: {
        padding: '15px',
        backgroundColor: patientAccessTheme.palette.grey[100],
        border: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
        borderRadius: '8px',
        gap: '10px',
        display: 'flex',
        flexDirection: 'column',
      },
      // Inset container for the common sub-metric options block
      submetricOptionsBlock: {
        padding: '12px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        margin: '12px',
      },
      tooltip: {
        backgroundColor: patientAccessTheme.palette.grey[900],
        padding: '10px',
        fontSize: '13px',
        fontFamily: 'Inter',
        fontWeight: 400,
        lineHeight: '16px',
        marginTop: '2px',
        color: patientAccessTheme.palette.status.white,
      },
      arrow: {
        color: patientAccessTheme.palette.grey[900],
        marginLeft: '50px',
      },
      popper: {
        marginTop: '-4px',
        marginLeft: '-34px',
      },
    })();

    const quaterlyReportStore = useStores().quaterlyReportStore;
    const {
      incurredStartDate,
      incurredEndDate,
      paidStartDate,
      paidEndDate,
      isIncurredDate,
      isPaidDate,
      aggregationParameter,
      noDateFilter,
      minIncurred,
      maxIncurred,
      minPaid,
      maxPaid,
    } = quaterlyReportStore;
    const navigate = useNavigate();
    const payload = useAPIPayload();

    const [activeStep, setActiveStep] = useState<number>(0);
    const [timeGrain, setTimeGrain] = useState<string>(modalTimeGrain ?? '');
    const [isSecondaryAxis, setIsSecondaryAxis] = useState<boolean>(
      modalIsSecondaryAxis ?? false,
    );

    // ── Default factory ──────────────────────────────────────────────────────
    // submetrics is now SubmetricEntry[] — each entry has { name, color }
    const defaultOption = (): any => ({
      name: '',
      color: getRandomColor(),
      submetrics: [] as SubmetricEntry[],
    });

    const [primaryAxisData, setPrimaryAxisData] = useState<trendSeriesData>(
      modalPrimaryAxisData ?? {
        unit: '',
        chartType: isCohortComparison ? 'Line' : '',
        metrics: [{ name: '', identifier: '', options: [defaultOption()] }],
        chartStyle: isCohortComparison ? 'Solid Line' : '',
      },
    );
    const [secondaryAxisData, setSecondaryAxisData] = useState<trendSeriesData>(
      modalSecondaryAxisData ?? {
        unit: '',
        chartType: '',
        metrics: [{ name: '', identifier: '', options: [defaultOption()] }],
        chartStyle: '',
      },
    );
    const [metricOptions, setMetricOptions] = useState<any>([]);
    const [commonMetricOptions, setCommonMetricOptions] = useState<string[]>(
      [],
    );
    const [metricOptionsLoading, setMetricOptionsLoading] = useState(true);

    const isChartSeriesValid = (axisData: trendSeriesData) => {
      const { unit, chartType, chartStyle, metrics } = axisData;
      if (!unit || !chartType || !chartStyle) {
        return false;
      }
      return metrics.some(
        (m) =>
          m.name.trim() !== '' &&
          m.options.some(
            (opt) => opt.name.trim() !== '' && opt.color.trim() !== '',
          ),
      );
    };

    const handleSubmitDisability =
      timeGrain &&
      isChartSeriesValid(primaryAxisData) &&
      (!isSecondaryAxis || isChartSeriesValid(secondaryAxisData));

    const handleSubmit = () => {
      isModal
        ? handleModalSubmit(
            timeGrain,
            primaryAxisData,
            isSecondaryAxis,
            secondaryAxisData,
          )
        : navigate(TREND_ANALYSIS_INTERNAL),
        {
          state: {
            timeGrain,
            isSecondaryAxis,
            primaryAxisData,
            secondaryAxisData,
          },
        };
    };

    const getTrendMetricOptions = async () => {
      setMetricOptionsLoading(true);
      try {
        const res = await axios.post(
          properties.aiqBaseUrl +
            properties.deepDiveBaseUrl +
            properties.trendMetricOptions,
          payload,
        );
        setMetricOptions(res?.data?.data?.metric_options ?? []);
        setCommonMetricOptions(res?.data?.data?.common_submetric_options ?? []);
      } finally {
        setMetricOptionsLoading(false);
      }
    };

    useEffect(() => {
      getTrendMetricOptions();
    }, [
      isIncurredDate,
      isPaidDate,
      incurredStartDate,
      incurredEndDate,
      paidStartDate,
      paidEndDate,
      aggregationParameter,
      noDateFilter,
    ]);

    // ── ChartConfigDropdowns ─────────────────────────────────────────────────
    const ChartConfigDropdowns: React.FC<ChartConfigDropdownsProps> = ({
      axisLabel,
      axisData,
      setAxisData,
      otherAxisData,
      isSecondary,
      showSwitch,
      switchChecked,
      onSwitchChange,
    }) => {
      const [filteredUnitOptions, setFilteredUnitOptions] = useState<any>([]);
      const [filteredChartTypes, setFilteredChartTypes] = useState<any>([]);
      const [filteredChartStyles, setFilteredChartStyles] = useState<any>([]);

      useEffect(() => {
        setFilteredUnitOptions(
          metricOptions
            .filter((item: any) => item.unit !== otherAxisData.unit)
            .map((item: any) => item.unit),
        );
        setFilteredChartTypes(
          getFilteredChartTypes(otherAxisData.chartType, isSecondaryAxis),
        );
        setFilteredChartStyles(
          getFilteredChartStyles(
            axisData.chartType,
            otherAxisData.chartType,
            isSecondaryAxis,
          ),
        );
      }, [otherAxisData, axisData]);

      return (
        <Grid
          className={isSecondary ? classes.metricBuilder : classes.primaryAxis}
          style={isSecondary ? { width: '50%', paddingLeft: '30px' } : {}}
        >
          <Grid className={classes.wrap}>
            <Typography
              fontSize="1rem"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin=" 0 0 0.5rem 0"
            >
              {axisLabel}
            </Typography>
            {showSwitch && (
              <Switch checked={switchChecked} handleChange={onSwitchChange} />
            )}
          </Grid>
          {(!isSecondary || switchChecked) && (
            <>
              <ReportCreateNewLabelField
                label={TrendAnalysisConstants.UNIT}
                component={
                  <Dropdown
                    label={TrendAnalysisConstants.UNIT}
                    dropDownOptions={filteredUnitOptions}
                    dropDownValues={filteredUnitOptions}
                    value={axisData.unit}
                    setValue={(value: string) =>
                      setAxisData((prev) => ({
                        ...prev,
                        unit: value,
                        metrics: [
                          {
                            name: '',
                            identifier: '',
                            options: [defaultOption()],
                          },
                        ],
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '290px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) =>
                      setAxisData((prev) => ({
                        ...prev,
                        unit: event.target.value as string,
                        metrics: [
                          {
                            name: '',
                            identifier: '',
                            options: [defaultOption()],
                          },
                        ],
                      }))
                    }
                    error={!axisData.unit}
                  />
                }
              />
              {!isCohortComparison && (
                <Grid
                  className={classes.wrap}
                  style={{ justifyContent: 'left', gap: '15px' }}
                >
                  <ReportCreateNewLabelField
                    label={TrendAnalysisConstants.CHART_TYPE}
                    component={
                      <Dropdown
                        label={TrendAnalysisConstants.CHART_TYPE}
                        dropDownOptions={filteredChartTypes}
                        dropDownValues={filteredChartTypes}
                        value={axisData.chartType}
                        setValue={(value: string) => {
                          const resetStyle =
                            isSecondary && value === 'Column'
                              ? 'Grouped Column'
                              : '';
                          setAxisData((prev) => ({
                            ...prev,
                            chartType: value,
                            chartStyle: resetStyle,
                          }));
                        }}
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '290px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) => {
                          const value = event.target.value as string;
                          const resetStyle =
                            isSecondary && value === 'Column'
                              ? 'Grouped Column'
                              : '';
                          setAxisData((prev) => ({
                            ...prev,
                            chartType: value,
                            chartStyle: resetStyle,
                          }));
                        }}
                        error={!axisData.chartType}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={TrendAnalysisConstants.CHART_STYLE}
                    component={
                      <Dropdown
                        label={TrendAnalysisConstants.CHART_STYLE}
                        dropDownOptions={filteredChartStyles}
                        dropDownValues={filteredChartStyles}
                        value={axisData.chartStyle}
                        setValue={(value: string) =>
                          setAxisData((prev) => ({
                            ...prev,
                            chartStyle: value,
                          }))
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '290px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          setAxisData((prev) => ({
                            ...prev,
                            chartStyle: event.target.value as string,
                          }))
                        }
                        error={!axisData.chartStyle}
                      />
                    }
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      );
    };

    // ── MetricConfig ─────────────────────────────────────────────────────────
    const MetricConfig: React.FC<MetricConfigProps> = ({
      axisData,
      setAxisData,
      isSecondary,
      selectedMetricOptions,
    }) => {
      // ── Metric-level handlers ────────────────────────────────────────────
      const handleAddMetric = () =>
        setAxisData((prev) => ({
          ...prev,
          metrics: [
            ...(prev.metrics || []),
            { name: '', identifier: '', options: [defaultOption()] },
          ],
        }));

      const handleDeleteMetric = (metricIndex: number) =>
        setAxisData((prev) => {
          const updated = [...(prev.metrics || [])];
          updated.splice(metricIndex, 1);
          return { ...prev, metrics: updated };
        });

      const handleMetricTypeChange = (index: number, value: string) => {
        const metric = selectedMetricOptions.metrics.find(
          (temp: any) => temp.type === value,
        );
        setAxisData((prev) => {
          const updated = [...prev.metrics];
          updated[index] = {
            ...updated[index],
            name: value,
            identifier: metric.identifier,
            options: [defaultOption()],
          };
          return { ...prev, metrics: updated };
        });
      };

      // ── Option (sub-metric row) handlers ─────────────────────────────────
      const handleAddSubmetric = (metricIndex: number) =>
        setAxisData((prev) => {
          const updatedMetrics = [...(prev.metrics || [])];
          const target = { ...updatedMetrics[metricIndex] };
          target.options = [...(target.options || []), defaultOption()];
          updatedMetrics[metricIndex] = target;
          return { ...prev, metrics: updatedMetrics };
        });

      const handleDeleteSubMetric = (
        metricIndex: number,
        optionIndex: number,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...(prev.metrics || [])];
          const target = { ...updatedMetrics[metricIndex] };
          const updatedOptions = [...(target.options || [])];
          updatedOptions.splice(optionIndex, 1);
          target.options = updatedOptions;
          updatedMetrics[metricIndex] = target;
          return { ...prev, metrics: updatedMetrics };
        });

      /** Update a scalar field ('name' | 'color') on a specific option */
      const handleLevel2Change = (
        metricIndex: number,
        optionIndex: number,
        field: string,
        value: string,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...prev.metrics];
          const updatedOptions = [...updatedMetrics[metricIndex].options];
          updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            [field]: value,
          };
          updatedMetrics[metricIndex].options = updatedOptions;
          return { ...prev, metrics: updatedMetrics };
        });

      // ── Common submetric entry (SubmetricEntry) handlers ─────────────────

      /** Add a new blank submetric entry { name: '', color: randomColor } */
      const handleAddCommonSubmetric = (
        metricIndex: number,
        optionIndex: number,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...prev.metrics];
          const updatedOptions = [...updatedMetrics[metricIndex].options];
          const current = updatedOptions[optionIndex] as any;
          updatedOptions[optionIndex] = {
            ...current,
            submetrics: [
              ...(current.submetrics ?? []),
              { name: '', color: getRandomColor() } as SubmetricEntry,
            ],
          };
          updatedMetrics[metricIndex].options = updatedOptions;
          return { ...prev, metrics: updatedMetrics };
        });

      /** Update the `name` of a specific submetric entry */
      const handleChangeCommonSubmetricName = (
        metricIndex: number,
        optionIndex: number,
        submetricIndex: number,
        value: string,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...prev.metrics];
          const updatedOptions = [...updatedMetrics[metricIndex].options];
          const current = updatedOptions[optionIndex] as any;
          const updatedSubmetrics = [
            ...(current.submetrics ?? []),
          ] as SubmetricEntry[];
          updatedSubmetrics[submetricIndex] = {
            ...updatedSubmetrics[submetricIndex],
            name: value,
          };
          updatedOptions[optionIndex] = {
            ...current,
            submetrics: updatedSubmetrics,
          };
          updatedMetrics[metricIndex].options = updatedOptions;
          return { ...prev, metrics: updatedMetrics };
        });

      /** Update the `color` of a specific submetric entry */
      const handleChangeCommonSubmetricColor = (
        metricIndex: number,
        optionIndex: number,
        submetricIndex: number,
        value: string,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...prev.metrics];
          const updatedOptions = [...updatedMetrics[metricIndex].options];
          const current = updatedOptions[optionIndex] as any;
          const updatedSubmetrics = [
            ...(current.submetrics ?? []),
          ] as SubmetricEntry[];
          updatedSubmetrics[submetricIndex] = {
            ...updatedSubmetrics[submetricIndex],
            color: value,
          };
          updatedOptions[optionIndex] = {
            ...current,
            submetrics: updatedSubmetrics,
          };
          updatedMetrics[metricIndex].options = updatedOptions;
          return { ...prev, metrics: updatedMetrics };
        });

      /** Remove a submetric entry by index */
      const handleDeleteCommonSubmetric = (
        metricIndex: number,
        optionIndex: number,
        submetricIndex: number,
      ) =>
        setAxisData((prev) => {
          const updatedMetrics = [...prev.metrics];
          const updatedOptions = [...updatedMetrics[metricIndex].options];
          const current = updatedOptions[optionIndex] as any;
          const updatedSubmetrics = [
            ...(current.submetrics ?? []),
          ] as SubmetricEntry[];
          updatedSubmetrics.splice(submetricIndex, 1);
          updatedOptions[optionIndex] = {
            ...current,
            submetrics: updatedSubmetrics,
          };
          updatedMetrics[metricIndex].options = updatedOptions;
          return { ...prev, metrics: updatedMetrics };
        });

      // ── Disability checks ────────────────────────────────────────────────
      const checkDisability = (
        metricType: string,
        currentMetric?: string,
      ): boolean => {
        const unitBlock = metricOptions.find(
          (item: any) => item.unit === axisData.unit,
        );
        if (!unitBlock) {
          return true;
        }
        if (metricType === 'matrix') {
          return unitBlock.metrics.length === axisData.metrics.length;
        }
        if (metricType === 'submatrix' && currentMetric) {
          const unitMetricOption = unitBlock.metrics.find(
            (m: any) => m.type === currentMetric,
          );
          const axisMetricOption = axisData.metrics.find(
            (m) => m.name === currentMetric,
          );
          return (
            (unitMetricOption?.options?.length ?? 0) ===
            (axisMetricOption?.options?.length ?? 0)
          );
        }
        return true;
      };

      /** Names already selected in the submetric list for a given option */
      const usedSubmetricNames = (submetrics: SubmetricEntry[]) =>
        submetrics.map((s) => s.name).filter(Boolean);

      /** Available common options not yet selected for this option row */
      const availableCommonOptions = (submetrics: SubmetricEntry[]) =>
        commonMetricOptions.filter(
          (o) => !usedSubmetricNames(submetrics).includes(o),
        );

      // ── Render ───────────────────────────────────────────────────────────
      return (
        <Grid
          className={isSecondary ? classes.metricBuilder : classes.primaryAxis}
          style={isSecondary ? { width: '50%', paddingLeft: '30px' } : {}}
        >
          {axisData.metrics.map((metric, index) => (
            <ReportCreateNewLabelField
              key={index}
              label={`${TrendAnalysisConstants.METRIC} ${index + 1}`}
              component={
                <Grid className={classes.metricSelection}>
                  {/* ── Metric type row ── */}
                  <Grid className={classes.wrap}>
                    <Dropdown
                      label={TrendAnalysisConstants.METRIC}
                      dropDownOptions={getMetricDropdownOptions(
                        axisData.unit,
                        axisData.metrics,
                        metric.name,
                        metricOptions,
                      )}
                      dropDownValues={getMetricDropdownOptions(
                        axisData.unit,
                        axisData.metrics,
                        metric.name,
                        metricOptions,
                      )}
                      value={metric.name || ''}
                      setValue={(value: string) =>
                        handleMetricTypeChange(index, value)
                      }
                      selectStyles={{
                        ...commonDropDownProps.selectStyles,
                        width: '300px',
                      }}
                      labelTop="10px"
                      onChange={(event: any) =>
                        handleMetricTypeChange(index, event.target.value)
                      }
                      error={!metric.name}
                    />
                    {!isCohortComparison && (
                      <Delete
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteMetric(index)}
                      />
                    )}
                  </Grid>

                  {/* ── Option rows (shown once a metric type is chosen) ── */}
                  {metric.name && (
                    <Grid className={classes.metricSelection}>
                      {metric.options.map((option, optionIndex) => {
                        const typedOption = option as any;
                        // submetrics is now SubmetricEntry[]
                        const submetrics: SubmetricEntry[] = (
                          typedOption.submetrics ?? []
                        ).map((s: any) =>
                          typeof s === 'string'
                            ? { name: s, color: getRandomColor(s) }
                            : s,
                        );
                        const canAddMore =
                          commonMetricOptions.length > 0 &&
                          submetrics.length < commonMetricOptions.length;

                        return (
                          <Grid key={optionIndex}>
                            {/* ── Sub-metric name + option color + delete ── */}
                            <LabelValue
                              label={`${TrendAnalysisConstants.SUB_METRIC} ${
                                optionIndex + 1
                              }`}
                              labelsMinWidth="100px"
                              value={
                                <Grid
                                  style={{
                                    display: 'flex',
                                    gap: '7px',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Dropdown
                                    label={`Sub Metric ${optionIndex + 1}`}
                                    dropDownOptions={getSubMetricDropdownOptions(
                                      axisData.unit,
                                      metric.name,
                                      metric.options,
                                      option.name,
                                      metricOptions,
                                    )}
                                    dropDownValues={getSubMetricDropdownOptions(
                                      axisData.unit,
                                      metric.name,
                                      metric.options,
                                      option.name,
                                      metricOptions,
                                    )}
                                    value={option.name || ''}
                                    setValue={(value: string) =>
                                      handleLevel2Change(
                                        index,
                                        optionIndex,
                                        'name',
                                        value,
                                      )
                                    }
                                    selectStyles={{
                                      ...commonDropDownProps.selectStyles,
                                      width: '290px',
                                    }}
                                  />
                                  {!isCohortComparison && (
                                    <ColorPickerCircle
                                      color={option.color || '#3b82f6'}
                                      onChange={(newColor) =>
                                        handleLevel2Change(
                                          index,
                                          optionIndex,
                                          'color',
                                          newColor,
                                        )
                                      }
                                    />
                                  )}
                                  {!isCohortComparison && (
                                    <Delete
                                      style={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        handleDeleteSubMetric(
                                          index,
                                          optionIndex,
                                        )
                                      }
                                    />
                                  )}
                                </Grid>
                              }
                            />

                            {/* ── Common submetric options block ──
                                Each row = { name (dropdown) + color (picker) + delete }
                                Shown only once a sub-metric name is selected. */}
                            {option.name && commonMetricOptions.length > 0 && (
                              <Grid className={classes.submetricOptionsBlock}>
                                {submetrics.map((submetric, submetricIndex) => (
                                  <ReportCreateNewLabelField
                                    key={submetricIndex}
                                    label={`Sub Metric Option ${
                                      submetricIndex + 1
                                    }`}
                                    component={
                                      <Grid
                                        style={{
                                          display: 'flex',
                                          gap: '7px',
                                          alignItems: 'center',
                                        }}
                                      >
                                        {/* Name dropdown */}
                                        <Dropdown
                                          label={`Sub Metric Option ${
                                            submetricIndex + 1
                                          }`}
                                          // Show only unselected options + current row's value
                                          dropDownOptions={[
                                            ...availableCommonOptions(
                                              submetrics,
                                            ),
                                            ...(submetric.name
                                              ? [submetric.name]
                                              : []),
                                          ]}
                                          dropDownValues={[
                                            ...availableCommonOptions(
                                              submetrics,
                                            ),
                                            ...(submetric.name
                                              ? [submetric.name]
                                              : []),
                                          ]}
                                          value={submetric.name || ''}
                                          setValue={(value: string) =>
                                            handleChangeCommonSubmetricName(
                                              index,
                                              optionIndex,
                                              submetricIndex,
                                              value,
                                            )
                                          }
                                          selectStyles={{
                                            ...commonDropDownProps.selectStyles,
                                            width: '300px',
                                          }}
                                          labelTop="10px"
                                          onChange={(event: any) =>
                                            handleChangeCommonSubmetricName(
                                              index,
                                              optionIndex,
                                              submetricIndex,
                                              event.target.value as string,
                                            )
                                          }
                                        />

                                        {/* Color picker — new for submetric entries */}
                                        <ColorPickerCircle
                                          color={
                                            submetric.color || getRandomColor()
                                          }
                                          onChange={(newColor) =>
                                            handleChangeCommonSubmetricColor(
                                              index,
                                              optionIndex,
                                              submetricIndex,
                                              newColor,
                                            )
                                          }
                                        />

                                        {/* Delete */}
                                        <Delete
                                          style={{ cursor: 'pointer' }}
                                          onClick={() =>
                                            handleDeleteCommonSubmetric(
                                              index,
                                              optionIndex,
                                              submetricIndex,
                                            )
                                          }
                                        />
                                      </Grid>
                                    }
                                    width="100%"
                                    marginBottom="4px"
                                  />
                                ))}

                                {canAddMore && (
                                  <Button
                                    color="primary"
                                    variant="text"
                                    height="32px"
                                    width="fit-content"
                                    label="Add Sub Metric Option"
                                    onClick={() =>
                                      handleAddCommonSubmetric(
                                        index,
                                        optionIndex,
                                      )
                                    }
                                    startIcon={
                                      <Add
                                        fill={
                                          patientAccessTheme.palette
                                            .primary[500]
                                        }
                                      />
                                    }
                                  />
                                )}
                              </Grid>
                            )}
                          </Grid>
                        );
                      })}

                      {((isCohortComparison && metric.options.length === 0) ||
                        !isCohortComparison) && (
                        <Button
                          color="primary"
                          variant="text"
                          height="36px"
                          width="fit-content"
                          label={TrendAnalysisConstants.ADD_SUB_METRIC}
                          onClick={() => handleAddSubmetric(index)}
                          startIcon={
                            <Add
                              fill={patientAccessTheme.palette.primary[500]}
                            />
                          }
                          disabled={checkDisability('submatrix', metric.name)}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              }
              width="100%"
              marginBottom="16px"
            />
          ))}

          {((isCohortComparison && axisData?.metrics?.length === 0) ||
            !isCohortComparison) && (
            <Button
              color="primary"
              variant="text"
              height="36px"
              width="fit-content"
              label={TrendAnalysisConstants.ADD_METRIC}
              onClick={handleAddMetric}
              startIcon={<Add fill={patientAccessTheme.palette.primary[500]} />}
              disabled={checkDisability('matrix')}
            />
          )}
        </Grid>
      );
    };

    // ── MetricBuilder ─────────────────────────────────────────────────────────
    const MetricBuilder = () => {
      const timeGrainOptions = getDynamicTimeGrainOptions({
        noDateFilter,
        incurredStartDate,
        incurredEndDate,
        paidStartDate,
        paidEndDate,
        isIncurredDate,
        isPaidDate,
        aggregationParameter,
        minIncurred,
        maxIncurred,
        minPaid,
        maxPaid,
      });

      return (
        <Grid className={classes.metricBuilder}>
          <LabelValue
            label={TrendAnalysisConstants.SELECTED_RANGE}
            value={
              <Grid
                style={{ display: 'flex', gap: '5px', alignItems: 'center' }}
              >
                <DateFilter showNoDateFilterOption={true} />
                {noDateFilter && (
                  <Tooltip
                    arrow
                    classes={{
                      tooltip: classes.tooltip,
                      arrow: classes.arrow,
                      popper: classes.popper,
                    }}
                    title={TrendAnalysisConstants.NO_DATE_FILTER}
                    placement="bottom-start"
                  >
                    <InfoOutlinedIcon
                      style={{
                        height: '16px',
                        width: '16px',
                        marginLeft: '0.5rem',
                        fill: patientAccessTheme.palette.status.icon,
                      }}
                    />
                  </Tooltip>
                )}
              </Grid>
            }
            labelsMinWidth="150px"
          />
          <ReportCreateNewLabelField
            label={TrendAnalysisConstants.TIME_GRAIN}
            component={
              <Dropdown
                label={TrendAnalysisConstants.TIME_GRAIN}
                dropDownOptions={timeGrainOptions}
                dropDownValues={timeGrainOptions}
                value={timeGrain}
                setValue={(value: string) => setTimeGrain(value)}
                selectStyles={{
                  ...commonDropDownProps.selectStyles,
                  width: '620px',
                }}
                labelTop="10px"
                onChange={(event: any) =>
                  setTimeGrain(event.target.value as string)
                }
                error={!timeGrain}
              />
            }
          />
          {metricOptionsLoading ? (
            <CircularLoader />
          ) : (
            <>
              <Grid className={classes.innerContainer}>
                <Typography
                  fontSize="12px"
                  lineHeight="24px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  fontType="medium"
                >
                  {TrendAnalysisConstants.SERIES_CONFIGURATION}
                </Typography>
              </Grid>
              <Grid style={{ display: 'flex' }}>
                <ChartConfigDropdowns
                  axisLabel={TrendAnalysisConstants.PRIMARY_AXIS}
                  axisData={primaryAxisData}
                  setAxisData={setPrimaryAxisData}
                  otherAxisData={secondaryAxisData}
                  isSecondary={false}
                  showSwitch={false}
                  switchChecked={true}
                  onSwitchChange={() => {}}
                />
                {!isCohortComparison && (
                  <ChartConfigDropdowns
                    axisLabel={TrendAnalysisConstants.SECONDARY_AXIS}
                    axisData={secondaryAxisData}
                    setAxisData={setSecondaryAxisData}
                    otherAxisData={primaryAxisData}
                    isSecondary={true}
                    showSwitch={true}
                    switchChecked={isSecondaryAxis}
                    onSwitchChange={() => {
                      isSecondaryAxis &&
                        setSecondaryAxisData({
                          unit: '',
                          chartType: '',
                          metrics: [
                            {
                              name: '',
                              identifier: '',
                              options: [defaultOption()],
                            },
                          ],
                          chartStyle: '',
                        });
                      setIsSecondaryAxis((prev) => !prev);
                    }}
                  />
                )}
              </Grid>
              <OrDivider
                text={TrendAnalysisConstants.METRIC_TAXONOMY}
                containerStyles={{ margin: '0px' }}
              />
              <Grid style={{ display: 'flex' }}>
                <MetricConfig
                  axisData={primaryAxisData}
                  setAxisData={setPrimaryAxisData}
                  isSecondary={false}
                  selectedMetricOptions={metricOptions.find(
                    (temp: any) => temp.unit === primaryAxisData.unit,
                  )}
                />
                {isSecondaryAxis && (
                  <MetricConfig
                    axisData={secondaryAxisData}
                    setAxisData={setSecondaryAxisData}
                    isSecondary={true}
                    selectedMetricOptions={metricOptions.find(
                      (temp: any) => temp.unit === secondaryAxisData.unit,
                    )}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      );
    };

    const getStepContent = () => {
      switch (activeStep) {
        case 0:
          return <MetricBuilder />;
        default:
          return <></>;
      }
    };

    return (
      <>
        <Grid container className={classes.root}>
          {!isModal && (
            <Grid className={classes.title}>
              <PageTitleAndSubTitle
                title={TrendAnalysisConstants.TITLE}
                subTitle={''}
              />
            </Grid>
          )}
          {isModal ? (
            <BasicModal
              open={open ?? false}
              handleClose={() => handleClose()}
              title={TrendAnalysisConstants.TITLE}
              isSaveButton={true}
              saveBtnText={
                isEdit
                  ? cohortComparisonMessages.UPDATE
                  : cohortComparisonMessages.CREATE
              }
              saveBtnAction={handleSubmit}
              saveBtnDisabled={!handleSubmitDisability}
              saveBtnStartIcon={
                <Stats stroke={patientAccessTheme.palette.status.white} />
              }
              isCloseBtn={true}
              height="auto"
              width="1410px"
            >
              <Grid className={classes.stepsGrid}>
                <Grid className={classes.content}>{getStepContent()}</Grid>
              </Grid>
            </BasicModal>
          ) : (
            <Grid className={classes.stepsGrid}>
              <TabStepper
                steps={TrendAnalysisConstants.TABS}
                stepWidth="100%"
                activeStep={activeStep}
                handleStepOnClick={(index) => setActiveStep(index)}
              />
              <Grid className={classes.content}>{getStepContent()}</Grid>
            </Grid>
          )}
        </Grid>
      </>
    );
  },
);

export default TrendAnalysisPage;
